'use client';

import type { CSSProperties } from 'react';
import { useSafePrefetch } from '../../../lib/hooks/use-safe-prefetch';
import { cn } from '../../../lib/utils';
import {
  LinkPaginationNavigationButton,
  LinkPaginationPageNumberButton,
  PaginationEllipsisItem,
  usePaginationRange,
} from '../../ui/link-pagination';
import { useProductListingPageState } from './use-product-listing-page-state';

export function ProductListingPagination({
  prefetchPage,
  pageCount,
  slug,
  className,
  style,
}: {
  prefetchPage: (pageNumber: number) => void;
  pageCount: number;
  slug: string;
  className?: string;
  style?: CSSProperties;
}) {
  const { page: currentPage, updatePage } = useProductListingPageState();
  const { pageNumbers, hasNextPage, hasPreviousPage } = usePaginationRange({
    currentPage,
    totalPages: pageCount,
  });
  const { safePrefetch } = useSafePrefetch(prefetchPage);

  // Helper function to create pagination URLs
  function createPageUrl(page: number): string {
    if (page === 1) {
      return `/collections/${slug}`;
    }
    return `/collections/${slug}?page=${page}`;
  }

  if (pageCount <= 1) {
    return null;
  }

  return (
    <div className={cn('flex justify-center', className)} style={style}>
      <nav
        className="relative z-0 inline-grid auto-cols-min grid-flow-col-dense gap-2 shadow-sm md:gap-3"
        aria-label="Pagination"
      >
        <LinkPaginationNavigationButton
          direction="previous"
          enabled={hasPreviousPage}
          href={hasPreviousPage ? createPageUrl(currentPage - 1) : undefined}
          onClick={() => hasPreviousPage && updatePage(currentPage - 1)}
          onTouchStart={() => {
            if (hasPreviousPage) {
              safePrefetch(currentPage - 1);
            }
          }}
          onMouseEnter={() => {
            if (hasPreviousPage) {
              safePrefetch(currentPage - 1);
            }
          }}
        />
        {pageNumbers.map((pageNumber: string | number, index: number) =>
          pageNumber === '...' ? (
            <PaginationEllipsisItem key={`ellipsis-${index}`} />
          ) : (
            <LinkPaginationPageNumberButton
              key={pageNumber}
              pageNumber={pageNumber as number}
              currentPage={currentPage}
              href={createPageUrl(pageNumber as number)}
              onClick={() => updatePage(pageNumber as number)}
              onTouchStart={() => {
                safePrefetch(pageNumber as number);
              }}
              onMouseEnter={() => {
                safePrefetch(pageNumber as number);
              }}
            />
          )
        )}
        <LinkPaginationNavigationButton
          direction="next"
          enabled={hasNextPage}
          href={hasNextPage ? createPageUrl(currentPage + 1) : undefined}
          onClick={() => hasNextPage && updatePage(currentPage + 1)}
          onTouchStart={() => {
            if (hasNextPage) {
              safePrefetch(currentPage + 1);
            }
          }}
          onMouseEnter={() => {
            if (hasNextPage) {
              safePrefetch(currentPage + 1);
            }
          }}
        />
      </nav>
    </div>
  );
}
